/**
 * Turning a course's stored schedule into something a person reads.
 *
 * `lms_course.timezone` holds an IANA region name from migration 061 —
 * `America/New_York`, never `EST`. That is the right thing to store and the
 * wrong thing to show: "6:00-7:30 PM America/New_York" is an identifier
 * leaking through the interface. Everything here exists to keep the typed
 * value in the data and a zone label people recognise on the screen.
 *
 * `meeting_time` is display text and many rows still end in a legacy `EST` or
 * `ET` caption written when the column was the only place a zone lived. It is
 * stripped here rather than rewritten in the database: the typed column is the
 * authority, and a stale caption is a rendering problem, not a data migration.
 */

/**
 * A trailing US zone caption on the display text: `EST`, `ET`, `CDT`, `HST`.
 * `PM` cannot match — every alternative ends in `T`.
 */
export const TRAILING_ZONE_CAPTION = /[\s·,]*\b(?:E|C|M|P|AK|H)[SD]?T\b\.?\s*$/i;

/** Region zones a section is plausibly scheduled in. The database accepts any
 *  region name; this is the picker, not the constraint, and the current value
 *  of a section is always offered even when it is not on the list. */
export const COURSE_TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "Europe/London",
  "Asia/Baghdad",
  "Asia/Beirut",
  "Asia/Tehran",
  "Asia/Dubai",
  "Asia/Karachi",
] as const;

/** The institutional zone, matching migration 061's column default. A section
 *  cannot be saved without one, so the form opens on this rather than blank. */
export const DEFAULT_COURSE_TIMEZONE = "America/New_York";

/**
 * The zone's own short name: `ET`, `CT`, `Saudi Arabia Time`.
 *
 * Generic rather than current: `ET` is true in July and November, where `EDT`
 * and `EST` are each true for part of a term and wrong for the rest of it.
 * Returns an empty string for anything the runtime cannot resolve, so a bad
 * value renders as nothing instead of as itself.
 */
export function courseTimezoneLabel(timezone: string | null | undefined): string {
  const zone = (timezone ?? "").trim();
  if (!zone) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "shortGeneric",
    }).formatToParts(new Date());
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/** The place, for a picker that has to separate two zones sharing a label —
 *  `America/New_York` and `America/Toronto` are both `ET`. */
export function courseTimezoneOptionLabel(timezone: string): string {
  const place = timezone.split("/").slice(-1)[0].replaceAll("_", " ");
  const label = courseTimezoneLabel(timezone);
  return label ? `${place} (${label})` : place;
}

/**
 * The one-line schedule: `6:00-7:30 PM ET`.
 *
 * Empty when there is no display schedule, so the caller decides between
 * "Not scheduled" and a dash.
 */
export function formatCourseSchedule(
  meetingTime: string | null | undefined,
  timezone: string | null | undefined,
): string {
  const schedule = (meetingTime ?? "").trim().replace(TRAILING_ZONE_CAPTION, "").trim();
  if (!schedule) return "";
  const label = courseTimezoneLabel(timezone);
  return label ? `${schedule} ${label}` : schedule;
}
